import React, { useState, useEffect } from 'react';
import './CustomImage.css';
import { endpoints } from '../../config/api';

// Base64 encoded 1x1 transparent pixel as ultimate fallback
const TRANSPARENT_PIXEL = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

const CustomImage = ({ 
  src, 
  alt, 
  className = '', 
  fallbackSrc = '/images/company-placeholder.svg',
  onError,
  priority = false, // New prop for above-the-fold images
  aspectRatio = null, // New prop for aspect ratio
  width = null, // New prop for explicit width
  height = null // New prop for explicit height
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      // Handle both string URLs and object URLs
      const imageUrl = typeof src === 'string' ? src : src?.url;
      const imageAlt = typeof src === 'string' ? alt : src?.altText || alt;

      if (!imageUrl) {
        console.log('CustomImage - No valid URL provided, using fallback');
        setImageSrc(fallbackSrc);
        setLoading(false);
        return;
      }

      try {
        console.log('CustomImage - Attempting to load image:', imageUrl);
        setLoading(true);
        setError(false);

        // Create a new Image object to test if the image exists
        const img = new Image();
        
        img.onload = () => {
          console.log('CustomImage - Image loaded successfully:', imageUrl);
          setImageSrc(imageUrl);
          setLoading(false);
        };

        img.onerror = (e) => {
          console.error('CustomImage - Error loading image:', imageUrl, e);
          setImageSrc(fallbackSrc);
          setError(true);
          setLoading(false);
        };

        img.src = imageUrl;
      } catch (err) {
        console.error('CustomImage - Exception while loading image:', err);
        setImageSrc(fallbackSrc);
        setError(true);
        setLoading(false);
      }
    };

    loadImage();
  }, [src, fallbackSrc, alt]);

  // Calculate aspect ratio styles
  const getAspectRatioStyle = () => {
    if (aspectRatio) {
      return { aspectRatio };
    }
    if (width && height) {
      return { aspectRatio: `${width} / ${height}` };
    }
    return {};
  };

  // Determine loading strategy
  const loadingStrategy = priority ? 'eager' : 'lazy';

  if (loading) {
    return (
      <div 
        className={`custom-image ${className || ''}`}
        style={getAspectRatioStyle()}
      >
        <div className="image-skeleton">
          <div className="skeleton-shimmer"></div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`custom-image ${className || ''}`}
      style={getAspectRatioStyle()}
    >
      <img
        src={imageSrc}
        alt={alt || 'Image'}
        onError={(e) => {
          console.error('CustomImage - Final image error:', e);
          setImageSrc(fallbackSrc);
          setError(true);
          onError?.(e);
        }}
        loading={loadingStrategy}
        width={width}
        height={height}
      />
    </div>
  );
};

export default CustomImage; 